import {
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import './tooltip.css'
import { useClampToViewport } from './useClampToViewport'

/**
 * A hover/focus label for a control that shows only an icon.
 *
 * Not the browser's `title`: that one is OS-styled, arrives after a delay
 * nobody chose, never appears for keyboard users, and is the one piece of
 * native chrome that slips into an otherwise fully-styled interface.
 *
 * It is also NOT InfoTip. A tooltip NAMES a control whose icon can't — it says
 * what the button is, and it is redundant to anyone who already knows. InfoTip
 * EXPLAINS something the interface can't show, holds a sentence or a paragraph,
 * and opens on click because you go looking for it. Using one for the other
 * gives you either prose that vanishes when the mouse moves, or a name you have
 * to click to read.
 *
 * The label is presentational here — the trigger carries its own `aria-label`,
 * so a screen reader gets the name once rather than twice.
 *
 * **It answers a pointer that hovers and a keyboard that arrives, and a finger
 * is neither.** A touch has no hover to end and leaves focus sitting on the
 * button it pressed, so a label opened by either would stand there over the
 * page until focus moved somewhere else — and the control most likely to carry
 * one is in a band somebody is reading past. So the pointer is asked what it
 * is, and focus is taken only where the browser says it is the visible kind:
 * pressing a control leaves `:focus` behind and matches `:focus-visible` not at
 * all, which is exactly the difference between arriving at a control and
 * pressing one.
 *
 * A touch device is therefore told nothing an icon does not say, which is the
 * cost of the icon rather than of this: a control whose name only a tooltip
 * carries is unnamed on a phone, and the answer to that is a word beside the
 * icon.
 */

/**
 * Whether a pointer of this kind can hover.
 *
 * A pen can, and takes its label away when it lifts. A finger cannot: it
 * arrives and leaves in the same press, so a label opened for one is a label
 * nothing closes.
 */
const hovers = (pointerType: string): boolean => pointerType !== 'touch'

/**
 * Whether focus ARRIVED at this control rather than being left on it by a press.
 *
 * The browser already draws that distinction for the focus ring, and it is the
 * same distinction here — a tooltip and a ring both mark where the keyboard is.
 */
const arrived = (target: EventTarget): boolean =>
  target instanceof Element && target.matches(':focus-visible')

/**
 * Whether the trigger already shows its name in words.
 *
 * A tooltip names a control its icon cannot, so where the word is on screen
 * beside the icon the tooltip would repeat it under the pointer. Read from what
 * is RENDERED rather than from a width: a band that drops its words where it is
 * narrow shows them by a rule of its own, and `innerText` leaves out exactly
 * what that rule hid.
 *
 * Words cut short are not shown. `innerText` reads past an ellipsis, so a box
 * that clips its text is asked separately — that is the case where the tooltip
 * is the only place the whole name can be read.
 */
const showsName = (host: HTMLElement, label: string): boolean => {
  const words = (text: string) => text.replace(/\s+/g, ' ').trim().toLowerCase()
  if (words(host.innerText) !== words(label)) return false
  const clips = (el: Element) =>
    el.scrollWidth > el.clientWidth &&
    getComputedStyle(el).overflowX !== 'visible'
  return !clips(host) && !Array.from(host.querySelectorAll('*')).some(clips)
}

export function Tooltip({
  label,
  placement = 'bottom',
  /**
   * Hold the label back while the control has something of its own open. A
   * tooltip names a control somebody is about to use; once that control has
   * opened a panel, the pointer is still on it and the name is sitting on top
   * of the panel's first line.
   */
  quiet = false,
  /** Take the width it is given, for a trigger that must be able to shrink. */
  block = false,
  children,
}: {
  label: string
  placement?: 'top' | 'bottom'
  quiet?: boolean
  block?: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const id = useId()
  const hostRef = useRef<HTMLSpanElement>(null)
  /* Kept on screen by the same clamp every floating thing in the app uses. A
     tooltip is centered on its trigger, so one near either edge overhangs by half
     its own width — and the triggers most likely to carry one are icon buttons at
     the end of a bar. */
  const panelRef = useRef<HTMLSpanElement>(null)
  const shown = open && !quiet

  /* Drawn on the body, so no band, panel or scroll region around the trigger
     can clip it or stack over it. It stands at the trigger's center, on the
     side asked for, measured before it is painted. Being on the body puts it
     outside any scope that re-points the type, so it carries the trigger's
     step and family with it: inside the give flow its label speaks at the
     flow's one size. */
  useLayoutEffect(() => {
    const host = hostRef.current
    const panel = panelRef.current
    if (!shown || !host || !panel) return
    const r = host.getBoundingClientRect()
    panel.style.left = `${r.left + r.width / 2}px`
    panel.style.top = `${placement === 'top' ? r.top : r.bottom}px`
    const scope = getComputedStyle(host)
    panel.style.setProperty('--text-xs', scope.getPropertyValue('--text-xs'))
    panel.style.fontFamily = scope.fontFamily
  }, [shown, placement])

  /* Placed once, so it closes rather than drifting off its trigger when the
     region holding the trigger scrolls or the window changes size. */
  useEffect(() => {
    if (!shown) return
    const close = () => setOpen(false)
    const scrolled = (e: Event) => {
      const host = hostRef.current
      if (e.target instanceof Node && host && e.target.contains(host)) close()
    }
    window.addEventListener('scroll', scrolled, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', scrolled, true)
      window.removeEventListener('resize', close)
    }
  }, [shown])

  useClampToViewport(panelRef, shown)

  return (
    <span
      ref={hostRef}
      className={`tooltip${block ? ' tooltip--block' : ''}`}
      onPointerEnter={(e) => {
        if (hovers(e.pointerType) && !showsName(e.currentTarget, label))
          setOpen(true)
      }}
      onPointerLeave={() => setOpen(false)}
      // Focus reaches this via the child, so the keyboard gets the same label
      // the mouse does — which is the whole reason not to use `title`.
      onFocus={(e) => {
        if (arrived(e.target) && !showsName(e.currentTarget, label))
          setOpen(true)
      }}
      onBlur={() => setOpen(false)}
    >
      {children}
      {shown
        ? createPortal(
            <span
              ref={panelRef}
              className="tooltip__pop"
              data-placement={placement}
              role="tooltip"
              id={id}
              aria-hidden
            >
              {label}
            </span>,
            document.body,
          )
        : null}
    </span>
  )
}
